import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { AuthSession } from './AuthProvider'
import { getAuthProvider } from './index'

interface AuthContextValue {
  session: AuthSession | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  error: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const provider = useMemo(() => getAuthProvider(), [])
  const [session, setSession] = useState<AuthSession | null>(() => provider.getSession())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const onFocus = () => setSession(provider.getSession())
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [provider])

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true)
      setError(null)
      try {
        const s = await provider.login(email, password)
        setSession(s)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'unknown')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [provider],
  )

  const logout = useCallback(async () => {
    await provider.logout()
    setSession(null)
  }, [provider])

  const value: AuthContextValue = { session, loading, login, logout, error }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
